// utils/units/convert.js
//
// Conversion between a unit and its measure's canonical unit, plus the
// composite split/join helpers. Dependency-free and vendored into keepr-web
// (see registry.js for the rule).

'use strict';

const { getUnit } = require('./registry');

function fail(code, message) {
	const err = new Error(message);
	err.code = code;
	return err;
}

function unitOrThrow(unitId) {
	const unit = getUnit(unitId);
	if (!unit) throw fail('invalid_unit', 'unknown unit "' + unitId + '"');
	return unit;
}

/** Magnitude in `unitId` -> magnitude in the measure's canonical unit. */
function toBase(value, unitId) {
	return unitOrThrow(unitId).toBase(value);
}

/** Magnitude in the canonical unit -> magnitude in `unitId`. */
function fromBase(base, unitId) {
	return unitOrThrow(unitId).fromBase(base);
}

// Two units of one measure that do or do not share a factor. A count converts
// to nothing but itself: tablets, puffs, drops and international units share
// the `count` measure so one picker can offer them, but there is no factor
// between any two -- "3 tablets in puffs" is a category error, not a rounding
// question. A RATE of a count keeps that rule for the thing counted and drops
// it for the time: 60 drops/min is 3,600 drops/h, and never any tablets.
function sharesFactor(from, to) {
	if (from.id === to.id) return true;
	if (from.numerator && from.numerator === to.numerator) return true;
	return from.convertible !== false && to.convertible !== false;
}

/**
 * True when convert() moves a value from `fromId` to `toId`: two known units
 * of one measure that share a factor. Never throws.
 */
function canConvert(fromId, toId) {
	const from = getUnit(fromId);
	const to = getUnit(toId);
	return !!from && !!to && from.measure === to.measure && sharesFactor(from, to);
}

// The comparison a bound written in `unitId` makes on `base`. A reciprocal
// unit (`decreasing`, registry.js) runs against its base -- "under
// 8 L/100km" is "base above 12.5 km/L" -- so its order operators turn
// around; equality, an unknown operator and every other unit pass through.
// Three spellings, for the three vocabularies that compare bases: KQL and the
// rule matcher ('<'), display rules and the automation thresholds ('lt'),
// and Mongo ('$lt').
const TURNED = {
	'<': '>', '<=': '>=', '>': '<', '>=': '<=',
	lt: 'gt', lte: 'gte', gt: 'lt', gte: 'lte',
	$lt: '$gt', $lte: '$gte', $gt: '$lt', $gte: '$lte',
};

/** True when a bigger number in `unitId` is a smaller base (a reciprocal unit). Never throws. */
function isDecreasing(unitId) {
	const unit = getUnit(unitId);
	return !!unit && unit.decreasing === true;
}

/** The operator that compares `base` the way `op` compares numbers in `unitId`. */
function baseOperator(op, unitId) {
	if (!isDecreasing(unitId)) return op;
	return Object.prototype.hasOwnProperty.call(TURNED, op) ? TURNED[op] : op;
}

/** Convert between two units of the same measure. Throws across measures. */
function convert(value, fromId, toId) {
	const from = unitOrThrow(fromId);
	const to = unitOrThrow(toId);
	if (from.measure !== to.measure) {
		throw fail('measure_mismatch', 'cannot convert ' + from.measure + ' (' + from.id + ') to ' + to.measure + ' (' + to.id + ')');
	}
	if (from.id === to.id) return value;
	if (!sharesFactor(from, to)) {
		throw fail('not_convertible', '"' + from.id + '" and "' + to.id + '" are counts of different things and do not convert');
	}
	return to.fromBase(from.toBase(value));
}

function compositeOrThrow(unitId) {
	const unit = unitOrThrow(unitId);
	if (!Array.isArray(unit.parts)) throw fail('invalid_unit', 'unit "' + unit.id + '" is not a composite unit');
	return unit;
}

// Snap away binary noise so 8.4375 lb-oz splits to 7 oz, not 6.999999999 oz.
const SNAP = 1e9;
function snap(n) {
	return Math.round(n * SNAP) / SNAP;
}

/**
 * A composite magnitude (decimal of the major part) -> { major, minor }.
 * The major part is the integer part; the minor part is the remainder in
 * the minor unit, always below minorPerMajor. Both carry the sign of the
 * input.
 */
function splitComposite(value, unitId) {
	const unit = compositeOrThrow(unitId);
	let major = Math.trunc(value);
	let minor = snap((value - major) * unit.minorPerMajor);
	// 2.9999999999999996 h is 2 h 60 min after the snap: carry it.
	if (Math.abs(minor) >= unit.minorPerMajor) {
		major += value < 0 ? -1 : 1;
		minor = 0;
	}
	return { major, minor };
}

/** { major, minor } -> the composite's decimal magnitude of its major part. */
function joinComposite(major, minor, unitId) {
	const unit = compositeOrThrow(unitId);
	const a = Number(major) || 0;
	const b = Number(minor) || 0;
	return a + b / unit.minorPerMajor;
}

module.exports = {
	toBase,
	fromBase,
	canConvert,
	isDecreasing,
	baseOperator,
	convert,
	splitComposite,
	joinComposite,
};
